import { Injectable, inject } from '@angular/core';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import {
  CheckoutOutcome,
  CheckoutPlan,
  CheckoutProvider,
  PlanPrice,
} from './checkout.service';
import { PurchaseBridgeService } from '../webview/purchase-bridge.service';
import { PosthogService } from './posthog.service';

/**
 * Achat par le MAGASIN, dans l'app native.
 *
 * C'est la voie conforme : la transaction se déroule dans la feuille d'achat
 * d'Apple ou de Google, jamais dans un navigateur extérieur. `CheckoutProvider`
 * a été écrit pour que cette classe prenne la place de Stripe sans qu'aucun
 * écran ne change (plan-technique-iap.md, étape B6).
 *
 * Ce qu'elle ne fait PAS, volontairement : répondre à « cet utilisateur est-il
 * premium ? ». Cette question se tranche dans notre base et le groupe Cognito.
 * Le magasin dit seulement qu'un achat a eu lieu ; c'est notre webhook qui
 * ouvre le droit. C'est ce qui nous garde libres de changer de prestataire.
 */
@Injectable({ providedIn: 'root' })
export class IapCheckoutService implements CheckoutProvider {
  private readonly bridge = inject(PurchaseBridgeService);
  private readonly posthog = inject(PosthogService);
  private readonly authApi = new AuthApi();

  isAvailable(): boolean {
    return this.bridge.isAvailable();
  }

  /**
   * Prix annoncés par le magasin pour CE pays.
   *
   * On garde `amount` et `currency` pour calculer l'économie annuelle, mais
   * c'est `priceString` que l'écran affiche : le magasin l'a déjà mis en forme
   * selon les conventions locales, et le reformater rouvrirait la porte à un
   * montant différent de celui facturé.
   */
  async getPrices(): Promise<PlanPrice[]> {
    const reply = await this.bridge.send('products');
    if (reply.status !== 'ok' || !reply.products?.length) return [];
    return reply.products
      .filter(
        (product) => product.plan === 'MONTHLY' || product.plan === 'YEARLY',
      )
      .map((product) => ({
        plan: product.plan as CheckoutPlan,
        amount: product.amount,
        currency: product.currency,
        priceString: product.priceString,
      }));
  }

  async purchase(plan: CheckoutPlan): Promise<CheckoutOutcome> {
    const userUid = this.userUid();
    if (!userUid) {
      // Sans notre identifiant, l'abonnement arriverait au webhook rattaché à
      // personne : l'utilisateur paierait sans rien recevoir. On refuse avant.
      throw new Error('no user uid: cannot attach a store purchase');
    }

    this.posthog.capture('checkout_started', { plan, provider: 'IAP' });
    const reply = await this.bridge.send('buy', { plan, userUid });

    switch (reply.status) {
      case 'ok':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      case 'pending':
        return 'pending';
      default:
        throw new Error(
          reply.message ?? `store purchase failed (${reply.status})`,
        );
    }
  }

  /**
   * « Gérer mon abonnement » ouvre le magasin — le seul endroit où un abonnement
   * intégré peut être résilié. Le back refuse d'ailleurs de l'annuler chez nous
   * (`STORE_MANAGED`) : le clore ici couperait l'accès pendant qu'Apple
   * continuerait de prélever.
   */
  async manageBilling(): Promise<void> {
    const reply = await this.bridge.send('manage');
    if (reply.status !== 'ok') {
      throw new Error(reply.message ?? 'cannot open store management');
    }
  }

  /**
   * Restauration des achats. **Obligatoire chez Apple** pour toute app vendant
   * un abonnement : sans elle, quelqu'un qui réinstalle l'app ne retrouve rien.
   *
   * @returns `true` si le magasin a retrouvé un achat.
   */
  async restore(): Promise<boolean> {
    const userUid = this.userUid();
    if (!userUid) throw new Error('no user uid: cannot restore');
    const reply = await this.bridge.send('restore', { userUid });
    if (reply.status !== 'ok') {
      throw new Error(reply.message ?? 'restore failed');
    }
    return Boolean(reply.purchased);
  }

  /** Notre identifiant applicatif, porté par le `sub` du jeton. */
  private userUid(): string | undefined {
    try {
      return this.authApi.decodeToken()?.sub as string | undefined;
    } catch {
      return undefined;
    }
  }
}
